import { useEffect, useMemo, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { Plus, ShoppingBag } from "lucide-react"
import { toast } from "sonner"

import { type MenuGridCategory, type MenuGridItem } from "@/components/features/menu/menu-grid"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import type { CancellationReasonOption } from "@/features/staff/cancellations"
import type { DiscountKindOption, DiscountReasonOption } from "@/features/staff/discounts"
import { OrderItemDrawer, errorMessage, useOrderDraft } from "@/features/staff/order-item-drawer"
import {
  CashDrawerClosedNotice,
  cashDrawerBlockedReason,
  cashDrawerBlocksOrders,
  useCashDrawer,
} from "@/features/staff/cash-drawer"
import type { RefundReasonOption } from "@/features/staff/refunds"
import type { ReopenReasonOption } from "@/features/staff/reopenings"
import {
  TableMenuBrowser,
  TableOrderPanel,
  paymentDisplayAmount,
  menuItemMatches,
  type TableAccountBill,
} from "@/features/staff/table-detail-workspace"
import { staffMutationResourceKey } from "@/features/staff/table-status"
import { t } from "@/lib/i18n"
import {
  patchInline,
  postInline,
  toastInlineUpdateError,
  toastInlineUpdateSuccess,
  type InlineUpdateResult,
} from "@/lib/inline-update"
import { normalizeForSearch } from "@/lib/search"

const I18N_BASE = "ui.react.features.staff.counter_order_workspace"

export type CounterCustomer = {
  name: string | null
  phone: string | null
}

type OrderErrors = Record<string, string | string[] | undefined>

type Props = {
  /** The open counter ticket, or null before the first line opens one. */
  bill: TableAccountBill | null
  customer: CounterCustomer
  categories: MenuGridCategory[]
  orphanItems: MenuGridItem[]
  orderErrors?: OrderErrors[]
  refundReasons?: RefundReasonOption[]
  canRefund?: boolean
  discountReasons?: DiscountReasonOption[]
  discountKinds?: DiscountKindOption[]
  canDiscount?: boolean
  canAuthorizeDiscount?: boolean
  cancellationReasons?: CancellationReasonOption[]
  reopenReasons?: ReopenReasonOption[]
  timeZone?: string
}

function buildIdempotencyKey() {
  return `counter-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Taking an order at the counter.
 *
 * Deliberately the table workspace with one card swapped: same three-column
 * grid, same menu browser, same account panel, same item drawer. A waiter who
 * knows one screen knows the other, and — more to the point — the account panel
 * is where the money and the mistakes are, so there is exactly one of it rather
 * than a second, thinner copy that drifts.
 *
 * What a table has and the counter does not is a table: no seats, no diners, no
 * occupancy, no QR. What the counter has instead is a person with a name, and
 * that is what the first card captures.
 */
export function CounterOrderWorkspace({
  bill,
  customer,
  categories,
  orphanItems,
  orderErrors,
  refundReasons = [],
  canRefund = false,
  discountReasons = [],
  discountKinds = [],
  canDiscount = false,
  canAuthorizeDiscount = false,
  cancellationReasons = [],
  reopenReasons = [],
  timeZone,
}: Props) {
  const [submitting, setSubmitting] = useState(false)
  const drawer = useOrderDraft(submitting)
  const submitLockRef = useRef(false)
  const actionLocksRef = useRef(new Set<string>())
  const [processingActionKeys, setProcessingActionKeys] = useState<Set<string>>(() => new Set())
  const [query, setQuery] = useState("")
  const [categoryFilter, setCategoryFilter] = useState<string>("all")
  const [orderTab, setOrderTab] = useState<"active" | "history">("active")
  const [mobileWorkspaceTab, setMobileWorkspaceTab] = useState<"menu" | "account">("menu")

  // The draft diner, held here only until a line stamps it onto the bill. Once
  // the ticket exists the bill owns the answer and this is no longer read —
  // `Location::OrderBuilder` only stamps the bill a line OPENS, so there is no
  // way to rename a ticket from here and no point pretending otherwise.
  const [draftName, setDraftName] = useState("")
  const [draftPhone, setDraftPhone] = useState("")
  const ticketIsOpen = bill !== null
  const customerName = ticketIsOpen ? customer.name ?? "" : draftName
  const customerPhone = ticketIsOpen ? customer.phone ?? "" : draftPhone

  // A ticket that just got settled or cancelled elsewhere comes back as no bill
  // at all. Start the next one clean rather than carrying the last diner's name
  // into it.
  useEffect(() => {
    if (ticketIsOpen) return
    setDraftName("")
    setDraftPhone("")
  }, [ticketIsOpen])

  const currentErrors = orderErrors?.[0] ?? {}
  const selectedItem = drawer.draft.item
  // Read off the shared prop, exactly as the table workspace does.
  const cashDrawerState = useCashDrawer()
  const cashDrawerBlocked = cashDrawerBlocksOrders(cashDrawerState)
  const cashDrawerReason = cashDrawerBlockedReason(cashDrawerState)

  const openBills = useMemo(() => (bill && bill.status === "open" ? [bill] : []), [bill])
  const activeBill = openBills[0] ?? null
  const activeOrders = useMemo(() => activeBill?.orders ?? [], [activeBill])
  // What is left to collect, not what the ticket says — same reading the table
  // workspace uses, and for the same reason: once staff charges part of a ticket
  // the two diverge and the button must name the smaller one.
  const activeAmount = paymentDisplayAmount(activeBill?.outstandingTotal ?? activeBill?.total ?? 0)
  const currency = activeBill?.currency ?? "MXN"

  const cartCountByItemId = useMemo(() => {
    const counts: Record<number, number> = {}
    const allItems = [...categories.flatMap((category) => category.items), ...orphanItems]
    for (const order of activeOrders) {
      const existingItem = allItems.find((item) => item.name === order.name)
      if (!existingItem) continue
      counts[existingItem.id] = (counts[existingItem.id] ?? 0) + order.quantity
    }
    return counts
  }, [activeOrders, categories, orphanItems])

  const filteredCategories = useMemo(() => {
    const q = normalizeForSearch(query.trim())
    return categories
      .map((category) => ({ ...category, items: category.items.filter((item) => menuItemMatches(item, q)) }))
      .filter((category) => {
        if (categoryFilter !== "all" && String(category.id) !== categoryFilter) return false
        return category.items.length > 0
      })
  }, [categories, query, categoryFilter])

  const filteredOrphanItems = useMemo(() => {
    const q = normalizeForSearch(query.trim())
    if (categoryFilter !== "all" && categoryFilter !== "orphan") return []
    if (!q) return orphanItems
    return orphanItems.filter((item) => menuItemMatches(item, q))
  }, [orphanItems, query, categoryFilter])

  const totalFilteredItems =
    filteredCategories.reduce((sum, category) => sum + category.items.length, 0) + filteredOrphanItems.length

  const handleMenuItemClick = (item: MenuGridItem) => {
    // The one guard a table does not need. A table is opened before it is
    // loaded; a counter ticket is opened BY its first line, so the name has to
    // exist before that line does or the ticket is born anonymous.
    if (!ticketIsOpen && draftName.trim().length === 0) {
      toast.error(t(`${I18N_BASE}.name_required`))
      document.getElementById("counter-customer-name")?.focus()
      return
    }
    drawer.openWith(item)
  }

  const handleSubmit = () => {
    if (submitLockRef.current) return
    // The last stop before the POST, not the guard — see the same check on the
    // table workspace. The button is already disabled and the server refuses
    // regardless.
    if (cashDrawerBlocked) {
      toast.error(cashDrawerReason ?? "")
      return
    }
    if (!selectedItem) {
      toast.error(t("ui.react.features.staff.table_detail_workspace.select_item_menu"))
      return
    }

    submitLockRef.current = true
    setSubmitting(true)
    postInline(
      "/orders",
      {
        source: "counter",
        orders: [
          {
            item_id: selectedItem.id,
            extra_ids: drawer.draft.extraIds,
            quantity: drawer.draft.quantity,
            notes: drawer.draft.notes,
            service_type: "pick_up",
            // Present from the second line on. Without it `OrderBuilder`
            // #resolve_bill opens a fresh bill for every line and the counter
            // ends the night with one ticket per dish.
            ...(activeBill ? { bill_id: activeBill.id } : {}),
            customer_name: customerName,
            customer_phone: customerPhone,
          },
        ],
      },
      { preserveState: true }
    )
      .then((result) => {
        drawer.setOpen(false)
        drawer.reset()
        if (result.message) toastInlineUpdateSuccess(result)
      })
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => {
        submitLockRef.current = false
        setSubmitting(false)
      })
  }

  const runResourceAction = (resourceKey: string, action: () => Promise<InlineUpdateResult>) => {
    if (actionLocksRef.current.has(resourceKey)) return

    actionLocksRef.current.add(resourceKey)
    setProcessingActionKeys((current) => new Set(current).add(resourceKey))
    action()
      .then((result) => toastInlineUpdateSuccess(result))
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => {
        actionLocksRef.current.delete(resourceKey)
        setProcessingActionKeys((current) => {
          const next = new Set(current)
          next.delete(resourceKey)
          return next
        })
      })
  }

  const resourceIsLoading = (resourceKey: string) => processingActionKeys.has(resourceKey)

  const handleCloseBill = (billId: number) => {
    runResourceAction(staffMutationResourceKey("bill", billId), () =>
      postInline(`/bills/${billId}/close`, {}, { preserveState: true })
    )
  }

  const handleCreatePaymentAttempt = (
    billId: number,
    paymentMethod: "cash" | "physical_card_terminal",
    orderIds: number[] = []
  ) => {
    runResourceAction(staffMutationResourceKey("bill", billId), () =>
      postInline(
        `/bills/${billId}/payment_attempts`,
        {
          payment_method: paymentMethod,
          tip_percentage: 0,
          idempotency_key: buildIdempotencyKey(),
          ...(orderIds.length > 0 ? { order_ids: orderIds } : {}),
        },
        { preserveState: true }
      )
    )
  }

  const handleResolvePaymentAttempt = (paymentAttemptId: number, status: "succeeded" | "failed") => {
    runResourceAction(staffMutationResourceKey("attempt", paymentAttemptId), () =>
      patchInline(`/payment_attempts/${paymentAttemptId}`, { status }, { preserveState: true })
    )
  }

  return (
    <MainContainer size="md">
      <div className="space-y-4 pb-6" data-testid="counter-order-workspace">
        <OrderItemDrawer
          open={drawer.open}
          onOpenChange={drawer.handleOpenChange}
          draft={drawer.draft}
          onDraftChange={drawer.setDraft}
          onSubmit={handleSubmit}
          submitting={submitting}
          errors={currentErrors}
          blockedReason={cashDrawerReason}
          testIdPrefix="counter"
        />

        <CounterWorkspaceHeader ticketIsOpen={ticketIsOpen} />

        <CashDrawerClosedNotice />

        <Tabs
          value={mobileWorkspaceTab}
          onValueChange={(value) => setMobileWorkspaceTab(value as "menu" | "account")}
          className="gap-3"
        >
          <div className="grid min-h-[34rem] grid-cols-1 items-stretch gap-4 lg:h-[calc(100vh-11rem)] lg:grid-cols-[244px_minmax(0,1fr)_340px]">
            <CounterCustomerCard
              name={customerName}
              phone={customerPhone}
              onNameChange={setDraftName}
              onPhoneChange={setDraftPhone}
              locked={ticketIsOpen}
              error={errorMessage(currentErrors.customer_name)}
            />

            <TabsList
              className="grid w-full grid-cols-2 self-start lg:hidden"
              aria-label={t("ui.react.features.staff.table_detail_workspace.workspace_sections")}
            >
              <TabsTrigger value="menu">{t("ui.react.features.staff.table_detail_workspace.menu")}</TabsTrigger>
              <TabsTrigger value="account">{t("ui.react.features.staff.table_detail_workspace.account")}</TabsTrigger>
            </TabsList>

            <TabsContent
              value="menu"
              forceMount
              className="m-0 min-h-0 min-w-0 overflow-hidden data-[state=inactive]:hidden lg:data-[state=inactive]:block"
            >
              <TableMenuBrowser
                categories={filteredCategories}
                orphanItems={filteredOrphanItems}
                allCategories={categories}
                query={query}
                onQueryChange={setQuery}
                categoryFilter={categoryFilter}
                onCategoryFilterChange={setCategoryFilter}
                cartCounts={cartCountByItemId}
                onItemClick={handleMenuItemClick}
                totalItems={totalFilteredItems}
              />
            </TabsContent>

            <TabsContent
              value="account"
              forceMount
              className="m-0 min-h-0 min-w-0 overflow-hidden data-[state=inactive]:hidden lg:data-[state=inactive]:block"
            >
              <TableOrderPanel
                tab={orderTab}
                onTabChange={setOrderTab}
                // A counter ticket is one bill and this screen loads only that
                // one. Settled tickets live at /bills, which is the screen built
                // to look back at them.
                closedBills={[]}
                activeBill={activeBill}
                openBills={openBills}
                activeOrders={activeOrders}
                amount={activeAmount}
                currency={currency}
                // Nothing here can ring for a waiter: there is no table to ring
                // from and nobody sitting at one.
                alertReasons={[]}
                onAcknowledgeWaiterCall={() => {}}
                tableIsMutating={false}
                resourceIsLoading={resourceIsLoading}
                onCloseBill={handleCloseBill}
                onPay={handleCreatePaymentAttempt}
                onResolvePayment={handleResolvePaymentAttempt}
                refundReasons={refundReasons}
                canRefund={canRefund}
                discountReasons={discountReasons}
                discountKinds={discountKinds}
                canDiscount={canDiscount}
                canAuthorizeDiscount={canAuthorizeDiscount}
                cancellationReasons={cancellationReasons}
                reopenReasons={reopenReasons}
                timeZone={timeZone}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </MainContainer>
  )
}

function CounterWorkspaceHeader({ ticketIsOpen }: { ticketIsOpen: boolean }) {
  return (
    <header className="flex items-center gap-2" data-testid="counter-workspace-header">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <ShoppingBag className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
        <h1 className="truncate text-base font-semibold tracking-tight">{t(`${I18N_BASE}.title`)}</h1>
      </div>

      {/* Only once a ticket exists — before that, this screen already IS the new
          ticket and the control would restart something nobody started. */}
      {ticketIsOpen ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 gap-2"
          onClick={() => router.visit("/orders/new")}
          data-testid="counter-new-ticket"
        >
          <Plus className="size-3.5" aria-hidden="true" />
          {t(`${I18N_BASE}.new_ticket`)}
        </Button>
      ) : null}
    </header>
  )
}

/**
 * Where the table's context rail sits on `/tables/:external_id`, and answering
 * the same question that rail does: who is this account for.
 *
 * Locked once the ticket is open, because the bill is what holds the answer from
 * that moment on — `Location::OrderBuilder` stamps the name onto the bill the
 * first line opens and never touches it again, so an editable field here would
 * be a control that silently does nothing.
 */
function CounterCustomerCard({
  name,
  phone,
  onNameChange,
  onPhoneChange,
  locked,
  error,
}: {
  name: string
  phone: string
  onNameChange: (value: string) => void
  onPhoneChange: (value: string) => void
  locked: boolean
  error: string | null
}) {
  return (
    <aside className="min-h-0" data-testid="counter-customer-card">
      <Card density="compact" className="h-full min-h-0 gap-0 overflow-y-auto">
        <CardHeader className="border-b pb-3">
          <CardTitle className="text-base">{t(`${I18N_BASE}.customer`)}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="counter-customer-name">{t(`${I18N_BASE}.customer_name`)}</Label>
            <Input
              id="counter-customer-name"
              value={name}
              onChange={(event) => onNameChange(event.target.value)}
              readOnly={locked}
              maxLength={120}
              autoComplete="off"
              placeholder={t(`${I18N_BASE}.customer_name_placeholder`)}
              aria-invalid={error ? true : undefined}
              data-testid="counter-customer-name"
            />
            {error ? <p className="text-destructive text-xs">{error}</p> : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="counter-customer-phone">{t(`${I18N_BASE}.customer_phone`)}</Label>
            <Input
              id="counter-customer-phone"
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(event) => onPhoneChange(event.target.value)}
              readOnly={locked}
              maxLength={40}
              autoComplete="off"
              placeholder={t(`${I18N_BASE}.customer_phone_placeholder`)}
              data-testid="counter-customer-phone"
            />
          </div>

          <p className="text-muted-foreground text-xs">
            {t(`${I18N_BASE}.${locked ? "customer_locked_hint" : "customer_hint"}`)}
          </p>
        </CardContent>
      </Card>
    </aside>
  )
}
